import React, { forwardRef } from 'react';
import { Row, Col, Input, Checkbox, Tooltip, Icon, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { Form } from '@ant-design/compatible';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import classNames from 'classnames';

import PropertyDetailWrapper from '~components/property-detail-wrapper';
import { onlyViewPropertyRole } from '~client/helpers/auth';
import { propertyStatus } from '~constants/listing-management';

const FormItem = Form.Item;
const TextArea = Input.TextArea;
const { confirm } = Modal;

// eslint-disable-next-line no-unused-vars
const SEOSetting = ({ property, afterSave, form }, _ref) => {
  const { t } = useTranslation();
  const { getFieldDecorator, validateFieldsAndScroll, getFieldsValue } = form;

  const touchedInputField = new Set();
  const [updatePropertyProfile] = useMutation(MUTATION_PROPERTY_PROFILE);

  function handleSave(callback = () => {}) {
    touchedInputField.add('metaTitle');
    touchedInputField.add('metaDescription');

    validateFieldsAndScroll(['metaTitle', 'metaDescription'], { force: true }, async errors => {
      if (errors || errors?.length) {
        confirm({
          title: t('Confirm'),
          content:
            property.status === propertyStatus.PUBLISHED
              ? t('cms.property.listing_management.seo_setting.validate.publish.required')
              : t('cms.property.listing_management.seo_setting.validate.save.required'),
          onOk: () => {},
          onCancel: () => {},
        });
        callback({ status: 'success', isUpdated: false });
        return;
      }
      const formData = getFieldsValue();
      try {
        const metaPayload = {
          metaTitleEnabled: formData.metaTitle.enabledTemplate,
          metaTitle: formData.metaTitle.value,
          metaDescriptionEnabled: formData.metaDescription.enabledTemplate,
          metaDescription: formData.metaDescription.value,
        };
        await updatePropertyProfile({
          variables: {
            input: {
              id: property.id,
              ...metaPayload,
            },
          },
        });

        const profile = Object.assign({}, property.profile, metaPayload);

        afterSave({ ...property, profile });

        callback({ status: 'success', isUpdated: true });
      } catch (error) {
        callback({ status: 'err', err: error });
      }
    });
  }

  return (
    <PropertyDetailWrapper
      property={property}
      t={t}
      isPublished={property.status === 'PUBLISHED'}
      onClickSave={handleSave}
      canEdit={!onlyViewPropertyRole()}
    >
      <div className="seo-setting">
        <h2 className="property-detail__subtitle">
          {t('cms.property.listing_management.seo_setting.title')}
        </h2>

        <div className="seo-setting__description">
          <Icon type="info-circle" />
          {t('cms.property.listing_management.seo_setting.description')}
        </div>
        <Form>
          <Row gutter={24}>
            <Col span={12}>
              <FormItem>
                {getFieldDecorator('metaTitle', {
                  valuePropName: 'values',
                  initialValue: property.profile
                    ? {
                        enabledTemplate: property.profile.metaTitleEnabled,
                        value: property.profile.metaTitle || '',
                      }
                    : {
                        enabledTemplate: true,
                        value: '',
                      },
                  rules: [
                    {
                      validator: (_rule, value, callback) => {
                        if (
                          !value.enabledTemplate &&
                          !value.value &&
                          touchedInputField.has('metaTitle')
                        ) {
                          return callback(t('cms.properties.edit.error.blank'));
                        }
                        if (!value.enabledTemplate && value.value.length > 200) {
                          return callback(
                            t('cms.property.listing_management.seo_setting.validate.length'),
                          );
                        }
                        callback();
                      },
                    },
                  ],
                })(
                  <SEOField
                    label={t('cms.edit.seo.label.meta_title')}
                    tooltipContent={property.seoTemplate?.metaTitle?.replace(
                      '[Property Name]',
                      property.name,
                    )}
                    onChange={({ value }) => {
                      if (value) {
                        touchedInputField.add('metaTitle');
                      }
                    }}
                  />,
                )}
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem required>
                {getFieldDecorator('metaDescription', {
                  valuePropName: 'values',
                  initialValue: property.profile
                    ? {
                        enabledTemplate: property.profile.metaDescriptionEnabled,
                        value: property.profile.metaDescription || '',
                      }
                    : {
                        enabledTemplate: true,
                        value: '',
                      },
                  rules: [
                    {
                      validator: (_rule, value, callback) => {
                        if (
                          !value.enabledTemplate &&
                          !value.value &&
                          touchedInputField.has('metaDescription')
                        ) {
                          return callback(t('cms.properties.edit.error.blank'));
                        }
                        if (!value.enabledTemplate && value.value.length > 500) {
                          return callback(
                            t('cms.property.listing_management.seo_setting.validate.length'),
                          );
                        }
                        callback();
                      },
                    },
                  ],
                })(
                  <SEOField
                    multiLine
                    limit={500}
                    tooltipContent={property.seoTemplate?.metaDescription?.replace(
                      '[Property Name]',
                      property.name,
                    )}
                    label={t('cms.edit.seo.label.meta_description')}
                    onChange={({ value }) => {
                      if (value) {
                        touchedInputField.add('metaDescription');
                      }
                    }}
                  />,
                )}
              </FormItem>
            </Col>
          </Row>
        </Form>
      </div>
    </PropertyDetailWrapper>
  );
};

function SEOField({ label, multiLine = false, values, onChange, limit = 200, tooltipContent }) {
  const { t } = useTranslation();

  const handleEnableEdit = event => {
    onChange({
      enabledTemplate: event.target.checked,
      value: values.value,
    });
  };
  const handleFieldInput = event => {
    onChange({
      enabledTemplate: values.enabledTemplate,
      value: event.target.value,
    });
  };
  return (
    <div>
      <div className="seo-setting__header">
        <span className="seo-setting__header__label ant-legacy-form-item-required">{label}:</span>

        <Checkbox onChange={handleEnableEdit} checked={values.enabledTemplate}>
          <Tooltip trigger="hover" title={tooltipContent}>
            <span className="seo-form__template-enabled">
              {t('cms.edit.seo.checkbox.label.template_enabled')}
            </span>
          </Tooltip>
        </Checkbox>
      </div>

      {multiLine ? (
        <TextArea
          rows={5}
          disabled={values.enabledTemplate}
          placeholder={values.enabledTemplate ? t('cms.edit.seo.input.disabled.placeholder') : null}
          autoComplete="off"
          value={
            values.enabledTemplate ? t('cms.edit.seo.input.disabled.placeholder') : values.value
          }
          onChange={handleFieldInput}
        />
      ) : (
        <Input
          disabled={values.enabledTemplate}
          placeholder={values.enabledTemplate ? t('cms.edit.seo.input.disabled.placeholder') : null}
          autoComplete="off"
          value={
            values.enabledTemplate ? t('cms.edit.seo.input.disabled.placeholder') : values.value
          }
          onChange={handleFieldInput}
        />
      )}

      <div
        className={classNames('seo-setting__footer', {
          'seo-setting__footer--has-error': values.value.length > limit,
        })}
      >
        <span>
          {values.value.length}/{limit}
        </span>
      </div>
    </div>
  );
}

const EnhancedFCForm = Form.create()(forwardRef(SEOSetting));
export default EnhancedFCForm;

const MUTATION_PROPERTY_PROFILE = gql`
  mutation CMS_Update_Property_Profile($input: UpdatePropertyProfileInput!) {
    updatePropertyProfile(input: $input) {
      metaTitle
      metaDescription
      metaDescriptionEnabled
      metaTitleEnabled
    }
  }
`;
